/**
 * IndexedDB persistence for in-progress capture drafts (capture-flow §6.4).
 *
 * Why: iOS Safari aggressively suspends backgrounded tabs and can evict
 * page state. The user might tap into a capture, get interrupted, and
 * come back to find the chip review wiped. Mirroring state to IndexedDB
 * on every meaningful mutation lets us recover on the next mount.
 *
 * Schema per the spec:
 *   {
 *     client_req_id, capture_method, scope, chips, observation_text,
 *     raw_transcript, extraction_metadata, pending_sync, tenant_id,
 *     created_at, updated_at
 *   }
 *
 * Object store keyed by client_req_id. 24-hour TTL — drafts older than
 * that get swept on the next mount so abandoned drafts don't accumulate.
 *
 * Audio blobs are NEVER persisted (capture-flow §2.3 privacy rule).
 * Recovery preserves text state only.
 */

const DB_NAME = 'known-accounts';
const DB_VERSION = 1;
const STORE = 'capture_drafts';
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

export interface CaptureDraft {
  client_req_id: string;
  capture_method: string;
  scope: { entity_id: string } | null;
  chips: {
    account_id: string | null;
    contact_ids: string[];
    creates: Array<{ tmp_id: string; name: string; type: string }>;
    relationships: Array<{ type: 'reports_to' | 'parent_of'; from: string; to: string }>;
    next_action: { text: string; due_date: string | null } | null;
  };
  observation_text: string;
  raw_transcript: string | null;
  extraction_metadata: Record<string, unknown>;
  pending_sync: boolean;
  tenant_id: string;
  created_at: string;
  updated_at: string;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    if (typeof indexedDB === 'undefined') {
      // Private mode / older browser without IDB. Persistence becomes
      // a no-op — the app still works, captures just lose recovery.
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'client_req_id' });
        store.createIndex('tenant_id', 'tenant_id', { unique: false });
        store.createIndex('updated_at', 'updated_at', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      console.error('IDB open failed', req.error);
      resolve(null);
    };
  });
  return dbPromise;
}

export async function saveDraft(draft: CaptureDraft): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(draft);
    tx.oncomplete = () => resolve();
    tx.onerror = () => {
      console.error('IDB save failed', tx.error);
      resolve();
    };
  });
}

export async function deleteDraft(client_req_id: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(client_req_id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => {
      console.error('IDB delete failed', tx.error);
      resolve();
    };
  });
}

/**
 * List drafts for the current tenant, freshest first. Drafts older
 * than the 24h TTL are silently deleted in the same pass — abandoned
 * drafts don't survive past the recovery window.
 */
export async function listDraftsForTenant(tenantId: string): Promise<CaptureDraft[]> {
  const db = await openDb();
  if (!db) return [];
  return new Promise<CaptureDraft[]>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const idx = store.index('tenant_id');
    const range = IDBKeyRange.only(tenantId);
    const req = idx.openCursor(range);
    const out: CaptureDraft[] = [];
    const now = Date.now();

    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor) {
        const draft = cursor.value as CaptureDraft;
        const ageMs = now - new Date(draft.updated_at).getTime();
        if (Number.isFinite(ageMs) && ageMs > TTL_MS) {
          // Sweep stale draft inline.
          cursor.delete();
        } else {
          out.push(draft);
        }
        cursor.continue();
      }
    };
    tx.oncomplete = () => {
      out.sort((a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );
      resolve(out);
    };
    tx.onerror = () => {
      console.error('IDB list failed', tx.error);
      resolve([]);
    };
  });
}
